import {
  PartEngineeringStatus,
  PartEngineeringType,
  UnitsPerPieceType,
} from '@prisma/client';
import { CurrentSite } from '@snap-types/actions/current-site';
import { RunRateType, ScrapYieldType } from '@snap-types/models/enums';
import {
  PartEngineeringWithOpsAndReqsUpsert,
  partEngineeringWithOpsAndReqsUpsertSchema,
} from '@snap-types/models/part-engineering';
import { Decimal } from '@snap-types/types/decimal';

import { VisualRunRateType, VisualScrapYieldType } from '../operation/enums';
import { VisualQuantityPerType } from '../requirement/enums';
import { VisualWorkOrderWithOpsAndReqs } from '.';
import { VisualWorkOrderStatus, VisualWorkOrderType } from './enums';
import { visToSnapWorkorderCompositeId } from './work-order-composite-id';

export const visToSnapWorkOrderType = {
  [VisualWorkOrderType.MASTER]: PartEngineeringType.ENGINEERING_MASTER,
  [VisualWorkOrderType.WORK_ORDER]: PartEngineeringType.WORK_ORDER,
  [VisualWorkOrderType.QUOTE]: PartEngineeringType.QUOTE,
};

export const visToSnapWorkOrderStatus = {
  [VisualWorkOrderStatus.CLOSED]: PartEngineeringStatus.CLOSED,
  [VisualWorkOrderStatus.CANCELLED]: PartEngineeringStatus.CANCELLED,
  [VisualWorkOrderStatus.RELEASED]: PartEngineeringStatus.RELEASED,
  [VisualWorkOrderStatus.FIRMED]: PartEngineeringStatus.FIRMED,
  [VisualWorkOrderStatus.UNRELEASED]: PartEngineeringStatus.UNRELEASED,
};

export const visToSnapScrapYieldType = {
  [VisualScrapYieldType.SCRAP]: ScrapYieldType.SCRAP,
  [VisualScrapYieldType.YIELD]: ScrapYieldType.YIELD,
};

export const visToSnapRunRateType = {
  [VisualRunRateType.DAYS_PER_LOAD]: RunRateType.DAYS_PER_LOAD,
  [VisualRunRateType.DAYS_PER_PC]: RunRateType.DAYS_PER_PC,
  [VisualRunRateType.HRS_PER_LOAD]: RunRateType.HRS_PER_LOAD,
  [VisualRunRateType.HRS_PER_PC]: RunRateType.HRS_PER_PC,
  [VisualRunRateType.LOADS_PER_DAY]: RunRateType.LOADS_PER_DAY,
  [VisualRunRateType.LOADS_PER_HR]: RunRateType.LOADS_PER_HR,
  [VisualRunRateType.MIN_PER_PC]: RunRateType.MIN_PER_PC,
  [VisualRunRateType.PCS_PER_DAY]: RunRateType.PCS_PER_DAY,
  [VisualRunRateType.PCS_PER_HR]: RunRateType.PCS_PER_HR,
  [VisualRunRateType.PCS_PER_MIN]: RunRateType.PCS_PER_MIN,
} as const;

export const visToSnapUnitsPerPieceType = {
  [VisualQuantityPerType.START]: UnitsPerPieceType.START,
  [VisualQuantityPerType.END]: UnitsPerPieceType.END,
};

export function visToSnapWorkOrder(
  wo: VisualWorkOrderWithOpsAndReqs,
  site: CurrentSite,
): PartEngineeringWithOpsAndReqsUpsert {
  return partEngineeringWithOpsAndReqsUpsertSchema.parse({
    ...visToSnapWorkorderCompositeId(wo),
    status: visToSnapWorkOrderStatus[wo.status],
    desiredQuantity: wo.desired_qty ?? new Decimal(0),
    receivedQuantity: wo.received_qty ?? new Decimal(0),
    drawing: wo.drawing_id,
    drawingRevision: wo.drawing_rev_no,
    estimatedMaterialCost: wo.est_material_cost,
    estimatedLaborCost: wo.est_labor_cost,
    estimatedBurdenCost: wo.est_burden_cost,
    estimatedServiceCost: wo.est_service_cost,
    actualMaterialCost: wo.act_material_cost,
    actualLaborCost: wo.act_labor_cost,
    actualBurdenCost: wo.act_burden_cost,
    actualServiceCost: wo.act_service_cost,
    partId: wo.part_id?.toUpperCase() ?? null,
    partOriginalSource: site.originalSource,
    siteId: wo.site_id.toUpperCase(),
    originalSource: site.originalSource,
    createdAt: wo.create_date,
    closeDate: wo.close_date,
    costedDate: wo.costed_date,
    engineeredDate: wo.engineered_date,
    operations: wo.operations.map((op) => ({
      engType: visToSnapWorkOrderType[op.workorder_type],
      engStatus: visToSnapWorkOrderStatus[op.status],
      baseId: op.workorder_base_id,
      lotId: op.workorder_lot_id,
      splitId: op.workorder_split_id,
      subId: op.workorder_sub_id,
      sequenceNum: op.sequence_no,
      resourceId: op.resource_id.toUpperCase(),
      runRate: op.run,
      runRateType: visToSnapRunRateType[op.run_type],
      estLoadSizeQty: op.load_size_qty,
      estStartQty: op.calc_start_qty,
      estEndQty: op.calc_end_qty,
      actCompletedQty: op.completed_qty,
      estSetupHrs: op.setup_hrs,
      estRunHrs: op.run_hrs,
      actSetupHrs: op.act_setup_hrs,
      actRunHrs: op.act_run_hrs,
      estLabSetupCostPerHr: op.setup_cost_per_hr,
      estLabRunCostPerHr: op.run_cost_per_hr,
      estRunCostPerUnit: op.run_cost_per_unit,
      estBurSetupCostPerHr: op.bur_per_hr_setup,
      estBurRunCostPerHr: op.bur_per_hr_run,
      estBurRunCostPerUnit: op.bur_per_unit_run,
      estBurPctSetup: op.bur_percent_setup,
      estBurPctRun: op.bur_percent_run,
      estBurPerOperation: op.bur_per_operation,
      estAtlLabTotal: op.est_atl_lab_cost,
      estAtlBurTotal: op.est_atl_bur_cost,
      estAtlSrvTotal: op.est_atl_ser_cost,
      estReqMatTotal: op.est_ttl_mat_cost,
      estReqLabTotal: op.est_ttl_lab_cost,
      estReqBurTotal: op.est_ttl_bur_cost,
      estReqSrvTotal: op.est_ttl_ser_cost,
      actAtlLabTotal: op.act_atl_lab_cost,
      actAtlBurTotal: op.act_atl_bur_cost,
      actAtlSrvTotal: op.act_atl_ser_cost,
      actReqMatTotal: op.act_ttl_mat_cost,
      actReqLabTotal: op.act_ttl_lab_cost,
      actReqBurTotal: op.act_ttl_bur_cost,
      actReqSrvTotal: op.act_ttl_ser_cost,
      estSrvBaseCharge: op.service_base_chg,
      estSrvMinCharge: op.service_min_chg,
      servicePartId: op.service_part_id?.toUpperCase() ?? null,
      serviceId: op.service_id?.toUpperCase() ?? null,
      siteId: op.site_id.toUpperCase(),
      originalSource: site.originalSource,
      quotingConfigHistoryIds: [],
      scrapFixedUnits: op.fixed_scrap_units,
      scrapYieldPercent: op.scrap_yield_pct,
      scrapYieldType: visToSnapScrapYieldType[op.scrap_yield_type],
      requirements: op.requirements.map((req) => ({
        engType: visToSnapWorkOrderType[req.workorder_type],
        engStatus: visToSnapWorkOrderStatus[req.status],
        baseId: req.workorder_base_id,
        lotId: req.workorder_lot_id,
        splitId: req.workorder_split_id,
        subId: req.workorder_sub_id,
        sequenceNum: req.operation_seq_no,
        pieceNum: req.piece_no,
        unitsPerPiece: req.qty_per,
        unitsPerPieceType: visToSnapUnitsPerPieceType[req.qty_per_type],
        scrapPercent: req.scrap_percent,
        usageUM: req.usage_um,
        estQty: req.calc_qty ?? new Decimal(0),
        actQty: req.issued_qty,
        estUnitMatCost: req.unit_material_cost,
        estUnitLabCost: req.unit_labor_cost,
        estUnitBurCost: req.unit_burden_cost,
        estBurPct: req.burden_percent ?? new Decimal(0),
        estBurPerUnit: req.burden_per_unit ?? new Decimal(0),
        estUnitSrvCost: req.unit_service_cost,
        estMatFixedCost: req.fixed_cost ?? new Decimal(0),
        estFixedQty: req.fixed_qty,
        estMatCostTotal: req.est_material_cost,
        estLabCostTotal: req.est_labor_cost,
        estBurCostTotal: req.est_burden_cost,
        estSrvCostTotal: req.est_service_cost,
        actMatCostTotal: req.act_material_cost,
        actLabCostTotal: req.act_labor_cost,
        actBurCostTotal: req.act_burden_cost,
        actSrvCostTotal: req.act_service_cost,
        siteId: req.site_id.toUpperCase(),
        originalSource: site.originalSource,
        reqPartId: req.part_id?.toUpperCase() ?? null,
        reqPartOriginalSource: site.originalSource,
        quotingConfigHistoryIds: [],
      })),
    })),
  });
}
